import React, { useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import ConfidenceLabel from "../../shared/ConfidenceLabel";

const MenuBar = ({ editor }) => {
  if (!editor) return null;
  const buttons = [
    { label: "G",       title: "Gras",           action: () => editor.chain().focus().toggleBold().run(),                  active: editor.isActive("bold"),           style: { fontWeight: "bold" } },
    { label: "I",       title: "Italique",        action: () => editor.chain().focus().toggleItalic().run(),                active: editor.isActive("italic"),         style: { fontStyle: "italic" } },
    { label: "S",       title: "Barré",           action: () => editor.chain().focus().toggleStrike().run(),                active: editor.isActive("strike"),         style: { textDecoration: "line-through" } },
    { label: "U",       title: "Souligné",        action: () => editor.chain().focus().toggleUnderline().run(),             active: editor.isActive("underline"),      style: { textDecoration: "underline" } },
    { label: "H1",      title: "Titre 1",         action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),   active: editor.isActive("heading", { level: 1 }) },
    { label: "H2",      title: "Titre 2",         action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),   active: editor.isActive("heading", { level: 2 }) },
    { label: "• List",  title: "Liste à puces",   action: () => editor.chain().focus().toggleBulletList().run(),            active: editor.isActive("bulletList") },
    { label: "1. List", title: "Liste numérotée", action: () => editor.chain().focus().toggleOrderedList().run(),           active: editor.isActive("orderedList") },
    { label: "❝",       title: "Citation",        action: () => editor.chain().focus().toggleBlockquote().run(),            active: editor.isActive("blockquote") },
    { label: "—",       title: "Séparateur",      action: () => editor.chain().focus().setHorizontalRule().run(),           active: false },
    { label: "↩",       title: "Annuler",         action: () => editor.chain().focus().undo().run(),                       active: false },
    { label: "↪",       title: "Rétablir",        action: () => editor.chain().focus().redo().run(),                       active: false },
  ];
  return (
    <div className="ph-menubar">
      {buttons.map((btn, i) => (
        <button key={i} title={btn.title}
          className={`ph-menubar-btn ${btn.active ? "ph-menubar-btn--active" : ""}`}
          style={btn.style || {}} onClick={btn.action} type="button">
          {btn.label}
        </button>
      ))}
    </div>
  );
};

// Composant éditeur isolé par page pour éviter les conflits de state
const PageEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: content || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "");
    }
  }, [content]);

  return (
    <div className="ph-editor-wrapper">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="ph-editor" />
    </div>
  );
};

const TabEvolution = ({ pages, activePage, setActivePage, onChangePage, onAddPage, onDeletePage, getPaginationItems, onChangeField, gc }) => {
  const page = pages[activePage] || {};
  const getC = gc || (() => ({ exists: false }));
 



  return (
    <div className="ph-evolution">

        {/* ── Pagination des pages ── */}

        <div className="ph-pages-nav">
        <div className="ph-pages-tabs">
            <button className="ph-nav-page-btn" onClick={() => setActivePage((p) => Math.max(0, p - 1))} disabled={activePage === 0}>‹</button>
            {getPaginationItems(pages.length, activePage).map((item, i) =>
            item === "..." ? (
                <span key={i} className="ph-ellipsis">…</span>
            ) : (
                <button key={i}
                className={`ph-page-tab ${activePage === item ? "ph-page-tab--active" : ""}`}
                onClick={() => setActivePage(item)}>
                {item + 1}
                </button>
            )
            )}
            <button className="ph-nav-page-btn" onClick={() => setActivePage((p) => Math.min(pages.length - 1, p + 1))} disabled={activePage === pages.length - 1}>›</button>
        </div>
        <button className="ph-add-page-btn" onClick={onAddPage}>
            + Ajouter une page
        </button>
        </div>
        {/* ── Champs anthropométriques ── */}
        <div className="ph-row">
        {[
            { key: "poids",  label: "Poids (kg)",  type: "number", step: "0.1",  placeholder: "Ex : 47"   },
            { key: "taille", label: "Taille (m)",  type: "number", step: "0.01", placeholder: "Ex : 1.64" },
            { key: "pb",     label: "PB (cm)",     type: "number", step: "0.1",  placeholder: "PB"        },
            { key: "pc",     label: "PC (cm)",     type: "number", step: "0.1",  placeholder: "PC"        },
            { key: "pn",     label: "PN (kg)",     type: "number", step: "0.1",  placeholder: "PN"        },
            { key: "zscore", label: "Z-score",     type: "text",   step: null,   placeholder: "Z-score"   },
        ].map(({ key, label, type, step, placeholder }) => (
            <div key={key} className="ph-field-group ph-field-group--small">
            <label>{label} <ConfidenceLabel confidence={getC(key)} /></label>
            <input
                type={type}
                step={step || undefined}
                placeholder={placeholder}
                value={page[key] ?? ""}
                onChange={(e) => onChangeField(key, e.target.value)}
                style={{ borderColor: getC(key).color || undefined }}
            />
            </div>
        ))}
        </div>
      {/* ── Éditeur de la page active ── */}
      <PageEditor
        key={activePage}
        content={pages[activePage]?.data || ""}
        onChange={(html) => onChangePage(activePage, html)}
      />

      {/* ── Supprimer page (sauf page 1) ── */}
      {activePage > 0 && (
        <button className="ph-delete-page-btn" onClick={() => onDeletePage(activePage)}>
          🗑 Supprimer cette page
        </button>
      )}
    </div>
  );
};

export default TabEvolution;